import { isFeatureAvailable, useGetDevFeatures } from "@components/featureGuard/FeatureGuard"
import { useAppDispatch, useAppSelector } from "@redux/app/hooks"
import { getCurrentCompany } from "@redux/features/currentCompany/currentCompanySlice"
import { getFacilities, setFacilities } from "@redux/features/facilities/facilitiesSlice"
import { getMapFiltersFacilities } from "@redux/features/mapFiltersFacilities/mapFiltersFacilitiesSlice"
import { getUserData } from "@redux/features/userData/userDataSlice"
import { FacilityType, GetAllFacilitiesArgs, PortalFeature } from "api"
import { useEffect } from "react"
import { useGetFacilitiesRadiusQuery } from "./dataGettterHooks/useGetFacilitiesRadiusQuery"

export const useGetVisibleFacilities = () => {
	const dispatch = useAppDispatch()
	const mapFilterFacilities = useAppSelector(getMapFiltersFacilities).facilities

	const currentCompany = useAppSelector(getCurrentCompany)

	const devFeatures = useGetDevFeatures()
	const userData = useAppSelector(getUserData)
	const facilitiesData = useAppSelector(getFacilities)

	const {
		isLoading: isFacilitiesWithRadiusDataLoading,
		isError: isFacilitiesWithRadiusDataError,
		data: facilitiesWithRadiusData,
	} = useGetFacilitiesRadiusQuery({
		isFacilitiesFeatureAvailable: isFeatureAvailable(
			undefined,
			PortalFeature.FACILITIES,
			currentCompany ?? "unknown company",
			devFeatures,
			userData.portalMode
		),
		types: (
			[
				"FISH_FARM",
				"PORT",
				"PLATFORM",
				"DISCHARGE_TERMINAL",
				"DISCHARGE_STS",
				"ANCHORAGE",
				"WIND_FARM",
				"WIND_SUBSTATION",
				"WIND_TURBINE",
			] as GetAllFacilitiesArgs["types"]
		)
			.filter((f) =>
				isFeatureAvailable(
					undefined,
					PortalFeature.FISH_FARM,
					currentCompany ?? "unknown",
					devFeatures,
					userData.portalMode
				) === false
					? f !== "FISH_FARM"
					: true
			)
			.filter((f) =>
				isFeatureAvailable(
					undefined,
					PortalFeature.OIL_AND_GAS,
					currentCompany ?? "unknown",
					devFeatures,
					userData.portalMode
				) === false
					? f !== "PLATFORM" &&
						f !== "DISCHARGE_TERMINAL" &&
						f !== "DISCHARGE_STS" &&
						f !== "ANCHORAGE"
					: true
			)
			.filter((f) =>
				isFeatureAvailable(
					undefined,
					PortalFeature.WIND_TURBINES,
					currentCompany ?? "unknown",
					devFeatures,
					userData.portalMode
				) === false
					? f !== "WIND_FARM" && f !== "WIND_SUBSTATION" && f !== "WIND_TURBINE"
					: true
			),
	})

	useEffect(() => {
		if (facilitiesWithRadiusData === null || facilitiesWithRadiusData === undefined) {
			return
		}
		const facilitiesTypeToInclude: FacilityType[] = []
		Object.entries(mapFilterFacilities).forEach(
			([key, value]) =>
				value === true &&
				(key.toLowerCase().includes("port") === true
					? facilitiesTypeToInclude.push("PORT")
					: key.toLowerCase().includes("fish") === true
						? facilitiesTypeToInclude.push("FISH_FARM")
						: key.toLowerCase().includes("platform") === true
							? facilitiesTypeToInclude.push("PLATFORM")
							: key.toLowerCase().includes("dischargeterminals") === true
								? facilitiesTypeToInclude.push("DISCHARGE_TERMINAL")
								: key.toLowerCase().includes("dischargests") === true
									? facilitiesTypeToInclude.push("DISCHARGE_STS")
									: key.toLowerCase().includes("anchorage") === true
										? facilitiesTypeToInclude.push("ANCHORAGE")
										: key.toLowerCase().includes("wind") === true
											? facilitiesTypeToInclude.push(
													"WIND_FARM",
													"WIND_SUBSTATION",
													"WIND_TURBINE"
												)
											: null)
		)
		const filteredFacilitiesWithRadiusData = facilitiesWithRadiusData.filter((f) =>
			facilitiesTypeToInclude.includes(f.type)
		)
		dispatch(setFacilities(filteredFacilitiesWithRadiusData))
	}, [facilitiesWithRadiusData, mapFilterFacilities])

	return { facilitiesData, isFacilitiesWithRadiusDataLoading }
}
